import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { withAuth } from '@cdk-prod/fortellis-auth-context';
import { withEntityContext } from '@cdk-prod/fortellis-entity-context';
import { useOktaAuth } from '@okta/okta-react';
import axios from 'axios';
import { environmentURLs } from './environment/CaptureEnvironment';
import {
  getOrGenerateIdentifierId,
  getOrgUnitId,
  resolveAnalyticsRoles
} from '../../utils/gtmUtils';

/**
 * Routes that should be excluded from Google Tag Manager analytics tracking.
 *
 * This includes admin sections, background/callback endpoints (login, billing),
 * and hidden/internal paths where tracking 'fortellis-pageview' events would
 * pollute the analytics data with non-user-facing traffic.
 */
const EXCLUDED_ROUTES = [
  '/admin/solutions',
  '/admin/leads',
  '/admin/review',
  '/admin/subscriptions-data',
  '/admin/betaUser049bc449',
  '/signingPreviewComplete',
  '/billingRedirect',
  '/login/callback',
  '/sell-rj4KHvaurUXMkdH8Tg',
  '/fake-login',
  '/storefront-X4K8ZIMeFnfZUptO'
];

function getPageSection(path) {
  if (path === '/') return 'Home';
  if (path.startsWith('/marketplace-account/mysolutions'))
    return 'My Solutions';
  if (path.startsWith('/marketplace-account')) return 'Marketplace Account';
  if (path.startsWith('/solutions')) return 'Solutions';
  return 'Marketplace';
}

export function GtmCoreDataLayer({ auth, entityContext }) {
  const okta = useOktaAuth();
  const authState =
    okta && 'authState' in okta
      ? okta.authState
      : auth?.isAuthenticated !== undefined
      ? { isAuthenticated: auth.isAuthenticated, isPending: false }
      : null;

  const location = useLocation();

  const prevEntityId = useRef(null);
  const prevUrl = useRef(null);
  const [userRoles, setUserRoles] = useState([]);
  const [rolesLoaded, setRolesLoaded] = useState(false);

  const userId = auth?.userData?.sub;
  const userEmail = auth?.userData?.email;
  const accessToken = auth?.accessToken;
  const isAuthenticated = authState?.isAuthenticated;
  const isAuthPending = authState?.isPending;

  const entity = entityContext?.entity;
  const entityId = entity?.id;
  const entityName = entity?.name;
  const entityOrgUnitId = entity?.orgUnitId;
  const entities = entityContext?.entities;
  const entitiesLoaded = entityContext?.entitiesLoaded;
  const loadingEntity = entityContext?.loadingEntity;

  const locationPathname = location?.pathname || '';
  const locationSearch = location?.search || '';

  // Fetch user profile roles when authenticated
  useEffect(() => {
    if (!isAuthenticated || !userId) {
      setUserRoles([]);
      setRolesLoaded(false);
      return;
    }

    let isMounted = true;
    const fetchUserRoles = async () => {
      try {
        const response = await axios.get(
          `${environmentURLs.accountsServerURL}/api/v1/users/profile/${userId}`,
          {
            headers: accessToken
              ? { Authorization: `Bearer ${accessToken}` }
              : {}
          }
        );
        if (isMounted) {
          const roles = response?.data?.profile?.roles;
          setUserRoles(Array.isArray(roles) ? roles : []);
          setRolesLoaded(true);
        }
      } catch (error) {
        if (isMounted) {
          console.error('Failed to fetch user roles for GTM data layer', error);
          setUserRoles([]);
          setRolesLoaded(true);
        }
      }
    };

    fetchUserRoles();

    return () => {
      isMounted = false;
    };
  }, [isAuthenticated, userId, accessToken]);

  useEffect(() => {
    // Wait until Okta has finished determining authentication state
    if (!authState || isAuthPending) {
      return;
    }

    // If authenticated, wait for user data, roles, and organization context to load
    if (
      isAuthenticated &&
      (!userEmail || !rolesLoaded || !entitiesLoaded || loadingEntity)
    ) {
      return;
    }

    const currentUrl = locationPathname + locationSearch;
    const fortellisId = isAuthenticated
      ? entityId || auth?.entityId || null
      : null;
    const orgName = isAuthenticated ? entityName || null : null;
    const orgUnitId = isAuthenticated
      ? getOrgUnitId(entity, entities, fortellisId)
      : null;
    const storeId = orgUnitId || null;
    const storeName = orgName || null;

    const urlChanged = currentUrl !== prevUrl.current;
    const isAccountSwitch = Boolean(
      fortellisId &&
        prevEntityId.current &&
        fortellisId !== prevEntityId.current
    );

    // Only push event if URL changed or account switched
    if (!urlChanged && !isAccountSwitch) {
      return;
    }

    // Update refs for next run
    if (urlChanged) prevUrl.current = currentUrl;
    if (fortellisId && fortellisId !== prevEntityId.current)
      prevEntityId.current = fortellisId;

    // Check if the current route is in the excluded list
    const isExcluded = EXCLUDED_ROUTES.some(route =>
      locationPathname.startsWith(route)
    );

    // Routes starting with /solutions are handled by SolutionDetailsGtmDataLayer
    if (locationPathname.startsWith('/solutions')) {
      return;
    }

    if (!isExcluded) {
      const identifierId = getOrGenerateIdentifierId();
      const fortellisUserId = isAuthenticated
        ? auth?.userData?.sub || null
        : null;
      const normalizedUserRoles = resolveAnalyticsRoles(userRoles);

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'fortellis-pageview',
        page_section: 'Marketplace',
        page_subsection: getPageSection(locationPathname),
        solutions_id: null,
        solution_name: null,
        solution_version: null,
        product: 'APP',
        application: 'Fortellis Network',
        identifier_id: identifierId,
        fortellis_user_id: fortellisUserId,
        user_id: null,
        enterprise_id: null,
        enterprise_name: null,
        company_id: null,
        store_id: storeId,
        store_name: storeName,
        stores: null,
        product_features: null,
        user_role: normalizedUserRoles,
        page_url: window.location.href,
        page_path: locationPathname,
        is_impersonated_session: false,
        fortellis_id: fortellisId,
        fortellis_org_name: orgName
      });
    }
  }, [
    locationPathname,
    locationSearch,
    auth,
    userEmail,
    userRoles,
    rolesLoaded,
    entity,
    entityId,
    entityName,
    entityOrgUnitId,
    entities,
    entitiesLoaded,
    loadingEntity,
    authState,
    isAuthPending,
    isAuthenticated
  ]);

  return null;
}

export default withAuth(withEntityContext(GtmCoreDataLayer));
